// What an organisation is to the firm, as a person reads it. A null kind is SAID — "Not yet known" —
// on every screen: left blank it read as "prospect" on the pursued list, and a guess dressed as a fact is
// the defect the kind decision exists to end (owner, 2026-09-17: Michael Page).

export const KIND_NOT_YET_KNOWN = 'Not yet known'

export const KIND_WORDS: Record<string, string> = {
  customer: 'Customer', prospect: 'Prospect', vendor: 'Vendor', partner: 'Partner', competitor: 'Competitor', other: 'Other',
}

/** The word for a kind; "Not yet known" for null, undefined or anything the vocabulary does not name. */
export function kindWord(kind: string | null | undefined): string {
  return kind && KIND_WORDS[kind] ? KIND_WORDS[kind] : KIND_NOT_YET_KNOWN
}

/**
 * The header's sentence after the kind: WHY, in the decision's own words when the API sent them ("partner — because 4 of its
 * people are on file as partner, and no thread says otherwise"), else what the kind means. A "because" that is not on file
 * is never invented: with no reason the line is the blurb alone.
 */
export function kindLede(kind: string | null | undefined, why: string | null | undefined): string {
  const blurb = kindBlurb(kind)
  const reason = (why ?? '').trim().replace(/\.$/, '')
  if (!reason) return `to the firm — ${blurb}.`
  return `to the firm — ${blurb}. Because ${reason.charAt(0).toLowerCase()}${reason.slice(1)}.`
}

/** What the kind means for the firm, in one line — the account header's own words. */
export function kindBlurb(kind: string | null | undefined): string {
  switch (kind) {
    case 'customer': return 'buys or bought the firm’s work'
    case 'prospect': return 'being courted'
    case 'vendor': return 'sells to the firm; its threads are never pursuits'
    case 'partner': return 'referral, co-delivery or alliance'
    case 'competitor': return 'a rival; everything it touches is a threat'
    case 'other': return 'placed as none of the above'
    default: return 'nothing on file decides it yet; the nightly tidy re-reads every thread'
  }
}

/** The CSS modifier for a kind tag: the kind itself, or `unknown` for null. */
export function kindClass(kind: string | null | undefined): string {
  return kind && KIND_WORDS[kind] ? kind : 'unknown'
}

// Which MARKET an organisation sits in (REQ-075): a sponsor, a lender, a corporate, an investment bank. Orthogonal
// to the kind, and said as "not yet known" until the evidence decides — never guessed from a name.

export const SEGMENT_WORDS: Record<string, string> = {
  sponsor: 'Private equity sponsor', 'private-credit': 'Private credit / lender', corporate: 'Corporate', 'investment-bank': 'Investment bank / advisor', other: 'Other',
}

/** The words for a segment; "Not yet known" for null or anything the vocabulary does not name. */
export function segmentWord(segment: string | null | undefined): string {
  return segment && SEGMENT_WORDS[segment] ? SEGMENT_WORDS[segment] : KIND_NOT_YET_KNOWN
}

/** The CSS modifier for a segment tag: the segment itself, or `unknown` for null. */
export function segmentClass(segment: string | null | undefined): string {
  return segment && SEGMENT_WORDS[segment] ? segment : 'unknown'
}
